// The small composer anchored to the clicked element: a note, optional dictation, Save.
import { Mic, MicOff } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { appendDictation, type SpeechCtor, type SpeechRecognitionLike } from "../../lib/feedback/speech";
import type { composerPosition } from "../../lib/feedback/mode";
import { FEEDBACK_UI_ATTR } from "../../lib/feedback/anchor";
import { cx, Spinner } from "../ui";
import { FbButton } from "./parts";

export const MAX_NOTE = 4000;

export interface ComposerProps {
  position: ReturnType<typeof composerPosition>;
  saving: boolean;
  error?: string | null;
  /** The browser's SpeechRecognition, if it has one. */
  speech: SpeechCtor | null;
  /** e.g. "Loan in view: this note is visible only to people who can see the loan." */
  scopeNote?: string | null;
  onSave: (note: string) => void;
  onCancel: () => void;
}

export function FeedbackComposer({ position, saving, error, speech, scopeNote, onSave, onCancel }: ComposerProps) {
  const [note, setNote] = useState("");
  const [listening, setListening] = useState(false);
  const rec = useRef<SpeechRecognitionLike | null>(null);
  const area = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    area.current?.focus();
    return () => rec.current?.stop();
  }, []);

  const trimmed = note.trim();
  const tooLong = trimmed.length > MAX_NOTE;
  const canSave = !!trimmed && !tooLong && !saving;

  const toggleDictation = () => {
    if (!speech) return;
    if (listening) {
      rec.current?.stop();
      return;
    }
    try {
      const r = new speech();
      r.lang = navigator.language || "en-US";
      r.continuous = true;
      r.interimResults = false;
      r.onresult = (e) => {
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const res = e.results[i];
          if (res.isFinal) setNote((n) => appendDictation(n, res[0].transcript));
        }
      };
      r.onerror = () => setListening(false);
      r.onend = () => setListening(false);
      rec.current = r;
      r.start();
      setListening(true);
    } catch {
      setListening(false);
    }
  };

  const save = () => {
    rec.current?.stop();
    if (canSave) onSave(trimmed);
  };

  const sheet = position.kind === "sheet";
  return (
    <div
      {...{ [FEEDBACK_UI_ATTR]: "" }}
      role="dialog"
      aria-label="Leave feedback on this element"
      className={cx(
        "pointer-events-auto fixed z-[80] border border-line bg-raised p-3 text-ink shadow-[var(--shadow-2)]",
        sheet ? "inset-x-0 bottom-0 rounded-t-[12px] pb-[max(12px,env(safe-area-inset-bottom))]" : "w-[340px] rounded-[10px]",
      )}
      style={sheet ? undefined : { left: position.left, top: position.top }}
    >
      <label htmlFor="feedback-note" className="eyebrow mb-1.5 block text-ink-2">
        Feedback
      </label>
      <textarea
        id="feedback-note"
        ref={area}
        value={note}
        rows={4}
        onChange={(e) => setNote(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            save();
          }
        }}
        placeholder="What should change here?"
        aria-invalid={tooLong || undefined}
        className="focus-ring block w-full resize-y rounded-md border border-input bg-surface px-2.5 py-2 text-[14px] leading-5 text-ink"
      />
      {scopeNote ? <p className="mt-1.5 text-[12px] text-ink-3">{scopeNote}</p> : null}
      {tooLong ? <p className="mt-1.5 text-[12px] text-danger">Keep it under {MAX_NOTE} characters.</p> : null}
      {error ? (
        <p role="alert" className="mt-1.5 text-[12px] text-danger">
          {error}
        </p>
      ) : null}
      <div className="mt-2 flex items-center gap-2">
        {speech ? (
          <button
            type="button"
            onClick={toggleDictation}
            aria-pressed={listening}
            aria-label={listening ? "Stop dictation" : "Dictate"}
            title={listening ? "Stop dictation" : "Dictate (your browser's speech service)"}
            className={cx(
              "focus-ring inline-flex h-11 w-11 items-center justify-center rounded-md border",
              listening ? "border-danger bg-danger-bg text-danger" : "border-line bg-surface text-ink-2 hover:bg-hover",
            )}
          >
            {listening ? <MicOff size={18} aria-hidden /> : <Mic size={18} aria-hidden />}
          </button>
        ) : null}
        <span className="flex-1" />
        <FbButton variant="ghost" onClick={onCancel}>
          Cancel
        </FbButton>
        <FbButton variant="primary" disabled={!canSave} onClick={save}>
          {saving ? <Spinner label="Saving" /> : null} Save
        </FbButton>
      </div>
    </div>
  );
}
